import { SESSION_SCOPED_STORAGE_PREFIXES } from '@shared/constants/storage-keys';
import type { CompanyDetail } from '@/modules/companies/types';
import type { Invoice } from '../types';

/**
 * Hand-off for the invoice PREVIEW TAB.
 *
 * The form opens its preview in a new browser tab so the unsaved form is never
 * touched. A new tab cannot read the form's React state, so the form writes the
 * already-built document (the same `Invoice` shape a saved invoice has, plus
 * the company record and template it renders with) to `localStorage` under a
 * one-off id, and the preview page reads it back by that id.
 *
 * Nothing here is sent to the server: opening a preview saves nothing and
 * reserves no invoice number. The payload prints the company's UNMASKED bank
 * account, so entries live only 30 minutes and are removed whenever the
 * session ends in this browser (`SESSION_SCOPED_STORAGE_PREFIXES`); within
 * that time the page keeps working on refresh because it only reads.
 *
 * Internal-only fields never reach this payload: `buildPreviewInvoice` sets
 * `memo_to_self: null` and `employee_contributions: []`.
 */
export interface InvoicePreviewPayload {
  invoice: Invoice;
  company: CompanyDetail | null;
  template: { config?: Record<string, unknown> | null } | null;
  createdAt: number;
}

const [PREFIX] = SESSION_SCOPED_STORAGE_PREFIXES;
const MAX_AGE_MS = 30 * 60 * 1000;

function createdAtOf(raw: string | null): number {
  try {
    return raw ? Number((JSON.parse(raw) as Partial<InvoicePreviewPayload>).createdAt) || 0 : 0;
  } catch {
    return 0;
  }
}

/**
 * Remove every preview past its 30 minutes (and any unreadable one). Runs when
 * Finance starts and whenever a preview is saved or read, so an expired entry
 * does not wait for the next preview to be opened.
 */
export function pruneInvoicePreviews(now = Date.now()): void {
  try {
    const keys = Array.from({ length: window.localStorage.length }, (_, index) =>
      window.localStorage.key(index),
    );
    for (const key of keys) {
      if (!key?.startsWith(PREFIX)) continue;
      const createdAt = createdAtOf(window.localStorage.getItem(key));
      if (!createdAt || now - createdAt > MAX_AGE_MS) window.localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable — pruning is best-effort.
  }
}

/** Store a preview and return its id, or `null` when storage is unavailable. */
export function saveInvoicePreview(
  payload: Omit<InvoicePreviewPayload, 'createdAt'>,
): string | null {
  const now = Date.now();
  pruneInvoicePreviews(now);
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${now}-${Math.random().toString(36).slice(2)}`;
  try {
    window.localStorage.setItem(PREFIX + id, JSON.stringify({ ...payload, createdAt: now }));
    return id;
  } catch {
    return null;
  }
}

export function readInvoicePreview(id: string): InvoicePreviewPayload | null {
  pruneInvoicePreviews();
  try {
    const raw = window.localStorage.getItem(PREFIX + id);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as InvoicePreviewPayload;
    if (!parsed?.invoice || Date.now() - parsed.createdAt > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}
